import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { internalAuth } from '../api/internalAdminEndpoints';
import {
  setInternalTokens,
  clearInternalTokens,
  hasStoredInternalRefreshToken,
  registerInternalAuthFailureHandler
} from '../api/internalAdminClient';

// Stage 5N-2B: identity context for the Glass-i internal-admin SPA
// surface. Completely independent from frontend/src/contexts/AuthContext —
// the two providers can coexist in the same React tree without sharing
// state. Each is consumed only by its own protected route.

const InternalAdminAuthContext = createContext({
  status: 'unknown',     // 'unknown' | 'unauthenticated' | 'authenticated'
  admin: null,           // { id, email, displayName, role, permissions, ... }
  permissions: [],
  hasPermission: () => false,
  login: async () => {},
  logout: async () => {},
  refreshMe: async () => {}
});

export function InternalAdminAuthProvider({ children }) {
  // Mirrors the tenant context posture: 'unknown' while we attempt to
  // rehydrate from sessionStorage; 'unauthenticated' if there's no
  // refresh token to start with.
  const [status, setStatus] = useState(
    hasStoredInternalRefreshToken() ? 'unknown' : 'unauthenticated'
  );
  const [admin, setAdmin] = useState(null);

  const refreshMe = useCallback(async () => {
    const data = await internalAuth.me();
    setAdmin(data);
    return data;
  }, []);

  const handleAuthFailure = useCallback(() => {
    setAdmin(null);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    registerInternalAuthFailureHandler(handleAuthFailure);
    return () => registerInternalAuthFailureHandler(null);
  }, [handleAuthFailure]);

  // Boot rehydration. If we have an internal refresh token in
  // sessionStorage, /me will trigger the 401-refresh interceptor when
  // the in-memory access token is empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasStoredInternalRefreshToken()) {
        if (!cancelled) setStatus('unauthenticated');
        return;
      }
      try {
        const data = await internalAuth.me();
        if (cancelled) return;
        setAdmin(data);
        setStatus('authenticated');
      } catch (_err) {
        if (cancelled) return;
        clearInternalTokens();
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const finishLogin = useCallback(async (loginPayload) => {
    setInternalTokens({
      accessToken:  loginPayload.accessToken,
      refreshToken: loginPayload.refreshToken
    });
    // The login response already carries `admin`, but we go through
    // /me one more time so the rehydrate path on hard reload is
    // testing exactly the same surface as the live state.
    const me = await internalAuth.me();
    setAdmin(me);
    setStatus('authenticated');
    return me;
  }, []);

  const login = useCallback(
    async ({ email, password }) => {
      const data = await internalAuth.login({ email, password });
      return finishLogin(data);
    },
    [finishLogin]
  );

  const logout = useCallback(async () => {
    try {
      await internalAuth.logout();
    } catch (_err) {
      /* token may already be invalid; clear local state anyway */
    }
    clearInternalTokens();
    setAdmin(null);
    setStatus('unauthenticated');
  }, []);

  const permissions = useMemo(
    () => (admin && Array.isArray(admin.permissions) ? admin.permissions : []),
    [admin]
  );

  const hasPermission = useCallback(
    (perm) => {
      if (!perm) return false;
      // super_admin's '*' isn't ever in `permissions` directly — the
      // backend expands it to the full list before returning. So a
      // simple includes() is enough.
      return permissions.includes(perm);
    },
    [permissions]
  );

  const value = useMemo(
    () => ({
      status,
      admin,
      permissions,
      hasPermission,
      login,
      logout,
      refreshMe
    }),
    [status, admin, permissions, hasPermission, login, logout, refreshMe]
  );

  return (
    <InternalAdminAuthContext.Provider value={value}>
      {children}
    </InternalAdminAuthContext.Provider>
  );
}

export default function useInternalAdminAuth() {
  return useContext(InternalAdminAuthContext);
}
